import { SIDE_PROFILE_MM, BOTTOM_PROFILE_MM } from './doorLeafGeometry.js';
import { RADIUS_SIDE_PROFILE_MM } from './profileGeometry.js';
import { FRAME_PROFILE_MM, THRESHOLDS } from './doorFrameGeometry.js';
import { CASSETTE } from './windowCassetteGeometry.js';
import { DEFAULT_WATERNOSE } from './waternoseGeometry.js';

export const DOOR_FAMILIES = [
  ['60_OUT', 'Smartline · outward'], ['60_IN', 'Smartline · inward'], ['60_PLUS', 'Smartline Plus'],
  ['MERANTI', 'Meranti'], ['COTTAGE', 'Cottage'], ['18_DEG', '18°']
];
export const geometryFamily = family => family === '60_OUT_PLUS' ? '60_PLUS' : family;
export const isPlusFamily = family => geometryFamily(family) === '60_PLUS';
export const isTimberFamily = family => ['MERANTI','COTTAGE','18_DEG'].includes(family);
// Matching 92 mm frame, measured from the timber surfaces (without shadow decals).
const TIMBER_FRAME = [[27,-0.5,0],[27,-52.5,2],[0,-52.5,2],[0,39.5,2],[40,39.5,2],[40,-0.5,2]];
const TIMBER_OAK = [[39.5,-30],[39.5,-48],[39.4,-48.62],[39.12,-49.17],[38.68,-49.62],[38.12,-49.9],[37.5,-50],[-50.5,-50],[-51.12,-49.9],[-51.67,-49.62],[-52.12,-49.17],[-52.4,-48.62],[-52.5,-48],[-52.5,-43.41],[-52.41,-42.81],[-52.14,-42.27],[-51.72,-41.83],[-51.18,-41.54],[-50.59,-41.42],[-0.5,-39],[-0.5,-30],[-0.4,-29.38],[-0.12,-28.82],[0.33,-28.38],[0.88,-28.1],[1.5,-28],[37.5,-28],[38.12,-28.1],[38.68,-28.38],[39.12,-28.82],[39.4,-29.38]].map(([z,y]) => [z, Number((y+50).toFixed(2))]);
const clone = value => JSON.parse(JSON.stringify(value));

export function doorFamilyDefaults(family = '60_OUT') {
  family = geometryFamily(family);
  if (!DOOR_FAMILIES.some(([id]) => id === family)) throw new Error('Unknown door family.');
  const timber = isTimberFamily(family), thicknessMm = timber ? 40 : 60;
  const frontMm = -52.5 + thicknessMm;
  // Match the source model: 40 mm timber leaf with a 1.5 mm side bevel.
  const side = timber ? [[0,-52.5,0],[Number((thicknessMm * 1.5 / 40).toFixed(4)),frontMm,0]] : RADIUS_SIDE_PROFILE_MM;
  const flat = [[0,-52.5,0],[0,frontMm,0]];
  const thresholds = Object.fromEntries(Object.entries(THRESHOLDS).map(([key,s]) => [key, { profiles: s.profiles, leafBottomMm: s.leafBottomMm }]));
  if (timber) {
    thresholds.threshold_pine = { profiles: [TIMBER_FRAME.map(([x,z,r]) => [z,x,r])], leafBottomMm: 29 };
    thresholds.threshold_oak = { profiles: [TIMBER_OAK], leafBottomMm: 14 };
  }
  return clone({ family, constructionVersion: 2, thicknessMm, opening: family === '60_IN' ? 'inward' : 'outward',
    finish: family === 'COTTAGE' ? 'untreated' : 'painted',
    threshold: isPlusFamily(family) || family === '60_IN' ? 'threshold_alu' : timber ? 'threshold_pine' : 'threshold_oak',
    sideProfileMm: side, topProfileMm: timber ? flat : side, bottomProfileMm: timber ? flat : BOTTOM_PROFILE_MM,
    frame: { profileMm: timber ? TIMBER_FRAME : FRAME_PROFILE_MM, leafInsetMm: 29 }, thresholds,
    cassette: { ...CASSETTE, depthMm: (thicknessMm - 8) / 2 },
    waternose: DEFAULT_WATERNOSE,
    hardware: { lockType: 'ASSA', hinges: 2 },
    centreMoulding: family === 'COTTAGE'
  });
}

export const measuredSideProfile = family => isTimberFamily(family) ? doorFamilyDefaults(family).sideProfileMm : SIDE_PROFILE_MM;
export const familyThreshold = (key, family) => isPlusFamily(family) ? 'threshold_alu' : key === 'threshold_std' ? (isTimberFamily(family) ? 'threshold_pine' : 'threshold_oak') : key === 'threshold_accoya' ? 'threshold_pine' : key;

// The first timber release provisionally used 47 mm. Upgrade only those old
// workspaces, including inactive family presets. Versioned custom sizes are
// left alone. Uniform contour scaling retains bevel angles and tangent radii.
export function migrateDoorConstruction(design) {
  design = { ...design, ...(design.family ? { family: geometryFamily(design.family) } : {}),
    ...(isPlusFamily(design.family) ? { threshold: 'threshold_alu' } : {}) };
  const upgrade = (input, family) => {
    if (!input || !isTimberFamily(family) || input.constructionVersion >= 2 || input.thicknessMm !== 47) return input;
    const ratio = 40 / 47, mm = value => Number(value.toFixed(6));
    const next = { ...input, constructionVersion: 2, thicknessMm: 40 };
    for (const key of ['sideProfileMm','topProfileMm','bottomProfileMm']) {
      if (Array.isArray(input[key])) next[key] = input[key].map(([x,z,r]) => [
        mm(x * ratio), mm(-52.5 + (z + 52.5) * ratio), ...(r === undefined ? [] : [mm(r * ratio)])
      ]);
    }
    if (input.cassette) {
      const depthMm = input.cassette.depthMm === 19.5 ? 16 : mm(input.cassette.depthMm * ratio);
      const grooveDepthMm = input.cassette.grooveDepthMm < depthMm ? input.cassette.grooveDepthMm
        : mm(input.cassette.grooveDepthMm * depthMm / input.cassette.depthMm);
      next.cassette = { ...input.cassette, depthMm, grooveDepthMm };
    }
    return next;
  };
  const next = upgrade(design, design.family);
  return design.familySettings ? { ...next, familySettings: Object.fromEntries(
    Object.entries(design.familySettings).map(([family, settings]) => [geometryFamily(family), { ...upgrade(settings, family), ...(isPlusFamily(family) ? { family: '60_PLUS', threshold: 'threshold_alu' } : {}) }])
  ) } : next;
}

// Workspace construction is separate from reusable/published design patterns.
export function switchDoorFamily(design, family) {
  family = geometryFamily(family);
  design = migrateDoorConstruction(design);
  const constructionKeys = Object.keys(doorFamilyDefaults());
  const familySettings = { ...design.familySettings, [design.family || '60_OUT']: Object.fromEntries(constructionKeys.map(k => [k, design[k]]).filter(([,v]) => v !== undefined)) };
  return { ...design, ...doorFamilyDefaults(family), ...familySettings[family], family, familySettings, ...(isPlusFamily(family) ? { threshold: 'threshold_alu' } : {}) };
}
